'use client'

import { useEffect, useCallback, useReducer, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/use-toast'
import { Database as DatabaseIcon, Eye, Edit, Sparkles } from 'lucide-react'
import { useContentData } from './useContentData'
import { useRole } from '@/lib/useRole'
import { revertContentToVersion } from './utils/contentUtils'
import { 
    contentDetailsReducer, 
    initialState 
} from './utils/contentDetailsReducer'
import { useBrands } from '@/lib/BrandsProvider'
import { supabase } from '@/lib/supabase'

// Components
import { ContentHeader } from './components/ContentHeader'
import { ContentPreview } from './components/ContentPreview'
import { ImprovementPanel } from './components/ImprovementPanel'
import { PublishStatusCard } from './components/PublishStatusCard'
import { OverviewTab } from './components/OverviewTab'
import { VersionHistoryDialog } from './components/dialogs/VersionHistoryDialog'
import { EditContentDialog } from './components/dialogs/EditContentDialog'
import { RawDataDialog } from './components/dialogs/RawDataDialog'
import { PublishHelpDialog } from './components/dialogs/PublishHelpDialog'
import { ChangeScheduleModal } from './components/ChangeScheduleModal'

/**
 * Main content details page component
 */
const ContentDetails = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { selectedBrand } = useBrands()
    const { 
        currentContent, 
        fetchContentDetails, 
        updateContentDetails,
        enhanceContentMedia
    } = useContentData()
    const { isSuperAdmin } = useRole()
    
    // State for current tab
    const [activeTab, setActiveTab] = useState<string>('preview')
    
    // State for enhance media functionality
    const [enhancingMedia, setEnhancingMedia] = useState(false)
    
    // State for exit edit confirmation
    const [exitEditHandler, setExitEditHandler] = useState<(() => void) | null>(null)
    console.log('exitEditHandler', exitEditHandler)

    // State for schedule change modal (for URL actions)
    const [showScheduleModal, setShowScheduleModal] = useState(false)

    // Check URL search parameters for actions from email template
    useEffect(() => {
        const action = searchParams.get('action')
        
        if (action) {
            switch (action) {
                case 'improve':
                    setActiveTab('improve')
                    break
                case 'cancel':
                case 'reschedule':
                    // Open the schedule change modal for both cancel and reschedule actions
                    setShowScheduleModal(true)
                    break
                default:
                    console.log('Unknown action parameter:', action)
                    break
            }
            
            // Clear the action parameter from URL after processing to avoid re-triggering
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.delete('action')
            const newURL = newSearchParams.toString() 
                ? `${window.location.pathname}?${newSearchParams.toString()}`
                : window.location.pathname
            navigate(newURL, { replace: true })
        }
    }, [searchParams, navigate])

    // Debug logs for platform data
    // useEffect(() => {
    //     console.log('Debug - Platform Data:', {
    //         brandId,
    //         platformKey,
    //         platformSettings,
    //         platformConnectionStatus,
    //         platformSettingsLoading
    //     })
    // }, [brandId, platformKey, platformSettings, platformConnectionStatus, platformSettingsLoading])

    // Use reducer for state management
    const [state, dispatch] = useReducer(contentDetailsReducer, initialState)
    
    // Load content data
    const loadContent = useCallback(async () => {
        if (!id) return null
        
        dispatch({ type: 'SET_LOADING', payload: true })
        try {
            const content = await fetchContentDetails(id)
            // console.log('Debug - Loaded Content:', content)
            dispatch({ type: 'SET_LOADING', payload: false })
            return content
        } catch (error) {
            console.error('Error loading content:', error)
            dispatch({ type: 'SET_LOADING', payload: false })
            return null
        }
    }, [id, fetchContentDetails])

    // Initial load
    useEffect(() => {
        if (id) {
            loadContent()
        }
    }, [id, loadContent])
    
    // Auto-refresh scheduled posts when processing
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;
        
        if (currentContent?.scheduledPostData?.status === 'PROCESSING') {
            // Refresh every 10 seconds while processing
            intervalId = setInterval(() => {
                loadContent() // This will refresh both content and scheduled post data
            }, 10000);
        }
        
        return () => {
            if (intervalId) {
                clearInterval(intervalId)
            }
        }
    }, [currentContent?.scheduledPostData?.status, loadContent])
    
    // Show toast notification for publishing errors
    useEffect(() => {
        const scheduledPostData = currentContent?.scheduledPostData;
        if ((scheduledPostData?.status === 'FAILED' || scheduledPostData?.status === 'error') && scheduledPostData.error_message) {
            toast({
                title: "Publishing Failed",
                description: scheduledPostData.error_message,
                variant: "destructive"
            })
        }
    }, [currentContent?.scheduledPostData?.status, currentContent?.scheduledPostData?.error_message])

    // Check if content belongs to selected brand
    useEffect(() => {
        if (currentContent && selectedBrand && currentContent.brand_id !== selectedBrand.id) {
            console.log('Content brand mismatch:', {
                contentBrandId: currentContent.brand_id,
                selectedBrandId: selectedBrand.id
            })
            toast({
                title: "Brand Mismatch",
                description: "This content belongs to a different brand. Redirecting to content list.",
                variant: "destructive"
            })
            navigate('/content')
        }
    }, [currentContent, selectedBrand, navigate])

    // Debug log for platform settings
    // useEffect(() => {
    //     console.log('Debug - Platform Settings:', {
    //         platformKey,
    //         currentPlatformSettings,
    //         matchingPlatform: Object.values(platformSettings).find(
    //             setting => {
    //                 const platformInfo = (setting as any).platforms;
    //                 return platformInfo?.platform_key === platformKey;
    //             }
    //         ),
    //         allPlatformSettings: platformSettings
    //     })
    // }, [platformKey, currentPlatformSettings, platformSettings])

    // Check if platform is connected based on settings
    
    // Find the platform ID for the connection status

    // Debug log for connection status
    // useEffect(() => {
    //     console.log('Debug - Connection Status:', {
    //         isPlatformConnected,
    //         isCheckingConnection,
    //         status: currentPlatformSettings?.status,
    //         platformKey,
    //         platformId
    //     })
    // }, [isPlatformConnected, isCheckingConnection, currentPlatformSettings, platformKey, platformId])

    // Handler for viewing a specific version
    const handleViewVersion = useCallback((versionNumber: number) => {
        dispatch({ type: 'VIEW_VERSION', payload: versionNumber })
    }, [])

    // Handler for reverting to a specific version
    const handleRevert = useCallback(async (versionNumber: number) => {
        if (!currentContent) return
        
        try {
            dispatch({ type: 'SET_LOADING', payload: true })
            
            // Optimistic UI update
            toast({
                title: "Reverting...",
                description: `Restoring version ${versionNumber} as the current version`,
            })
            
            // Call API to revert
            const success = await revertContentToVersion(currentContent.id, versionNumber)
                
            if (!success) throw new Error('Failed to revert version')
            
            // Successfully reverted
            toast({
                title: "Success!",
                description: `Version ${versionNumber} has been restored as the current version`,
            })
            
            // Reset version view state and refresh content
            dispatch({ type: 'RESET_VERSION_VIEW' })
            loadContent() // Force refresh
        } catch (error) {
            console.error('Error reverting version:', error)
            toast({
                title: "Error",
                description: "Failed to restore version. Please try again.",
                variant: "destructive"
            })
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false })
        }
    }, [currentContent, loadContent])

    // Check if viewing an old version
    const isViewingOldVersion = useCallback(() => {
        if (!currentContent || !state.versionView.isViewingVersion) return false
        if (state.versionView.versionNumber === currentContent.current_version) return false
        return true
    }, [currentContent, state.versionView])

    // Handle edit mode save
    const handleSaveEdit = useCallback(async () => {
        dispatch({ type: 'SET_EDIT_MODE', payload: false })
        await loadContent() // Force refresh to get the latest content
    }, [loadContent])

    // Handle edit mode cancel
    const handleCancelEdit = useCallback(() => {
        dispatch({ type: 'SET_EDIT_MODE', payload: false })
    }, [])

    // Handle enhance media
    const handleEnhanceMedia = useCallback(async (mediaType: 'image' | 'video' = 'image') => {
        if (!currentContent) return
        
        try {
            setEnhancingMedia(true)
            
            toast({
                title: "Enhancing Media...",
                description: `Finding and generating optimal ${mediaType === 'image' ? 'images' : 'videos'} for your content`,
            })
            
            await enhanceContentMedia(currentContent.id, mediaType)
            
            toast({
                title: "Media Enhanced!",
                description: `Your content has been updated with enhanced ${mediaType === 'image' ? 'images' : 'videos'}`,
            })
        } catch (error) {
            console.error('Error enhancing media:', error)
            toast({
                title: "Enhancement Failed",
                description: error instanceof Error ? error.message : "Failed to enhance media. Please try again.",
                variant: "destructive"
            })
        } finally {
            setEnhancingMedia(false)
        }
    }, [currentContent, enhanceContentMedia])

    // Handle content update
    const handleUpdateContent = useCallback(async (values: any) => {
        if (!currentContent) return
        
        try {
            await updateContentDetails(currentContent.id, {
                name: values.name,
                status: values.status
            })
            
            toast({
                title: "Updated",
                description: "Content details updated successfully",
            })
            
            // Reload content to get the latest changes
            loadContent()
        } catch (error) {
            console.error('Error updating content:', error)
            toast({
                title: "Error",
                description: "Failed to update content. Please try again.",
                variant: "destructive"
            })
            throw error
        }
    }, [currentContent, updateContentDetails, loadContent])

    const handleScheduleChange = useCallback(async (newScheduledFor: string) => {
        if (!currentContent) return
        
        try {
            const { error } = await supabase
                .from('scheduled_social_posts')
                .update({
                    scheduled_time: newScheduledFor,
                    updated_at: new Date().toISOString()
                })
                .eq('content_id', currentContent.id)
                .eq('status', 'SCHEDULED')
            
            if (error) {
                throw error
            }
            
            toast({
                title: "Schedule Updated",
                description: "Content schedule updated successfully",
            })
            
            // Reload content to get the updated schedule
            loadContent()
        } catch (error) {
            console.error('Error updating schedule:', error)
            toast({
                title: "Error",
                description: "Failed to update schedule. Please try again.",
                variant: "destructive"
            })
        }
    }, [currentContent, loadContent])

    // Render content not found state
    if (!currentContent) {
        return (
            <div className="container mx-auto py-6">
                <Card>
                    <CardContent className="flex flex-col items-center justify-center p-6">
                        <h3 className="text-lg font-medium">Content not found</h3>
                        <p className="text-sm text-muted-foreground mt-2">
                            The content you're looking for doesn't exist or you don't have permission to view it.
                        </p>
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            
            {/* Enhanced Header Section */}
            <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 border-b border-blue-200/50 shadow-sm">
                <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
                <div className="container p-0">
                    <div className="relative space-y-6 p-6">
                        {/* Desktop layout: side by side, Mobile layout: stacked */}
                        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 md:gap-4">
                            <ContentHeader 
                                content={currentContent}
                            />
                            
                            {/* Publish Status Card - below title on mobile, top-right on desktop */}
                            <div className="w-full md:w-auto md:flex-shrink-0">
                                <PublishStatusCard
                                    content={currentContent}
                                    versionNumber={state.versionView.isViewingVersion ? state.versionView.versionNumber : undefined}
                                    onShowPublishHelp={() => dispatch({ type: 'TOGGLE_PUBLISH_HELP' })}
                                    onContentUpdate={() => fetchContentDetails(currentContent.id)}
                                />
                            </div>
                        </div>
                    
                    </div>
                </div>
            </div>

            {/* Main content area with tabs */}
            <div className="flex-1">
                <div className="container p-0 h-full flex flex-col">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                        {/* Enhanced Tab List */}
                        <div className="px-6 pt-6">
                            <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm p-1 max-w-md">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-transparent to-purple-50/30" />
                                <TabsList className="relative grid w-full grid-cols-3 bg-transparent border-0 gap-1">
                                    <TabsTrigger value="preview" className="relative bg-white/40 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-white/60 transition-all duration-300 font-medium">
                                        <Eye className="h-4 w-4 mr-2" />
                                        Preview
                                    </TabsTrigger>
                                    <TabsTrigger value="edit" className="relative bg-white/40 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-white/60 transition-all duration-300 font-medium">
                                        <Edit className="h-4 w-4 mr-2" />
                                        Edit
                                    </TabsTrigger>
                                    <TabsTrigger value="improve" className="relative bg-white/40 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-white/60 transition-all duration-300 font-medium">
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        Improve
                                    </TabsTrigger>
                                </TabsList>
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1">
                            <TabsContent value="preview" className="h-full m-0">
                                <OverviewTab
                                    content={currentContent}
                                    versionNumber={state.versionView.isViewingVersion ? state.versionView.versionNumber : undefined}
                                    isViewingOldVersion={isViewingOldVersion()}
                                    onViewHistory={() => dispatch({ type: 'TOGGLE_VERSION_HISTORY' })}
                                    onResetVersionView={() => {
                                        loadContent()
                                        dispatch({ type: 'RESET_VERSION_VIEW' })
                                    }}
                                    onShowPublishHelp={() => dispatch({ type: 'TOGGLE_PUBLISH_HELP' })}
                                    onEnhanceMedia={handleEnhanceMedia}
                                    enhancingMedia={enhancingMedia}
                                    onScheduleChange={handleScheduleChange}
                                />
                            </TabsContent>

                            <TabsContent value="edit" className="h-full m-0">
                                <ContentPreview
                                    content={currentContent}
                                    editMode={true}
                                    versionNumber={state.versionView.isViewingVersion ? state.versionView.versionNumber : undefined}
                                    isViewingOldVersion={isViewingOldVersion()}
                                    onSaveEdit={handleSaveEdit}
                                    onCancelEdit={handleCancelEdit}
                                    onRestoreVersion={() => state.versionView.versionNumber && handleRevert(state.versionView.versionNumber)}
                                    onExitEditRequest={setExitEditHandler}
                                    onEnhanceMedia={handleEnhanceMedia}
                                    enhancingMedia={enhancingMedia}
                                />
                            </TabsContent>

                            <TabsContent value="improve" className="h-full m-0">
                                <ImprovementPanel
                                    content={currentContent}
                                    versionNumber={state.versionView.isViewingVersion ? state.versionView.versionNumber : undefined}
                                    error={state.error}
                                    onVersionCreated={(versionNumber) => {
                                        // Clear any previous errors
                                        dispatch({ type: 'SET_ERROR', payload: null })

                                        console.log('Version created:', versionNumber)
                                        
                                        // Fully reload the content to update the preview in real-time
                                        const updateContent = async () => {
                                            try {
                                                dispatch({ type: 'SET_LOADING', payload: true })
                                                const updatedContent = await loadContent() // Force refresh
                                                console.log('Updated content:', updatedContent)
                                                
                                                // Reset version view state
                                                dispatch({ type: 'RESET_VERSION_VIEW' })
                                            } catch (error) {
                                                console.error('Error updating content after version creation:', error)
                                            } finally {
                                                dispatch({ type: 'SET_LOADING', payload: false })
                                            }
                                        }
                                        
                                        updateContent()
                                    }}
                                    onExitImproveMode={() => setActiveTab('preview')}
                                    chatInitialFocus={state.shouldFocusChat}
                                    onChatFocused={() => dispatch({ type: 'SET_SHOULD_FOCUS_CHAT', payload: false })}
                                    onError={(message) => dispatch({ type: 'SET_ERROR', payload: message })}
                                />
                            </TabsContent>
                        </div>
                    </Tabs>
                </div>
            </div>

            {/* Enhanced Admin controls */}
            {isSuperAdmin && (
                <div className="relative overflow-hidden bg-gradient-to-br from-red-50/80 via-pink-50/60 to-white border-t border-red-200/50">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="relative p-4">
                        <div className="flex justify-center">
                            <Button
                                variant="destructive"
                                onClick={() => dispatch({ type: 'TOGGLE_DATA_MODAL' })}
                                className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                            >
                                <DatabaseIcon className="h-4 w-4 mr-2" />
                                [Superadmin Only] View Raw Data
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Dialogs */}
            <RawDataDialog
                content={currentContent}
                isOpen={state.isDataModalOpen}
                onClose={() => dispatch({ type: 'TOGGLE_DATA_MODAL' })}
            />

            <VersionHistoryDialog
                contentId={currentContent.id}
                isOpen={state.isVersionHistoryOpen}
                onClose={() => dispatch({ type: 'TOGGLE_VERSION_HISTORY' })}
                onViewVersion={handleViewVersion}
                initialVersion={state.newVersionNumber}
                onRevert={handleRevert}
            />

            <EditContentDialog
                content={currentContent}
                isOpen={state.isEditModalOpen}
                onClose={() => dispatch({ type: 'TOGGLE_EDIT_MODAL' })}
                onSave={handleUpdateContent}
            />

            <PublishHelpDialog
                isOpen={state.isPublishHelpOpen}
                onClose={() => dispatch({ type: 'TOGGLE_PUBLISH_HELP' })}
                contentType={currentContent.content_type}
            />

            <ChangeScheduleModal
                isOpen={showScheduleModal}
                onClose={() => setShowScheduleModal(false)}
                contentId={currentContent.id}
                currentScheduledTime={currentContent.scheduledPostData?.scheduled_for}
                onScheduleUpdate={loadContent}
            />
        </div>
    )
}

export default ContentDetails